import {printoutPalette} from "../../core/printoutPalette";
import type {PrintoutBlockTextStyle} from "../shared/blockAttributes";
import {
    parseEnumAttribute,
    parseTextStyleAttribute,
} from "../shared/blockAttributes";

export type FieldAlign = "left" | "center" | "right";
export type FieldLineStyle = "underline" | "box";

export type FieldTextStyle = PrintoutBlockTextStyle;

export const FIELD_ALIGNS: FieldAlign[] = ["left", "center", "right"];
export const FIELD_LINE_STYLES: FieldLineStyle[] = ["underline", "box"];

export const MIN_FIELD_HEIGHT_MM = 4;
export const MAX_FIELD_HEIGHT_MM = 40;
export const DEFAULT_FIELD_HEIGHT_MM = 10;
export const DEFAULT_FIELD_LINE_COLOR = printoutPalette.mutedText;
export const DEFAULT_FIELD_LABEL = "Field";

export interface PrintoutFieldAttrs {
    label: string;
    align: FieldAlign;
    labelStyle: FieldTextStyle;
    heightMm: number;
    lineStyle: FieldLineStyle;
    lineColor: string;
    fillColor: string | null;
}

const parseColor = (value: unknown, fallback: string | null): string | null => (
    typeof value === "string" && value.trim() ? value : fallback
);

export const clampFieldHeightMm = (value: unknown, fallback = DEFAULT_FIELD_HEIGHT_MM) => {
    const numberValue = typeof value === "number" ? value : Number(value);

    if (!Number.isFinite(numberValue)) return fallback;

    return Math.round(Math.min(MAX_FIELD_HEIGHT_MM, Math.max(MIN_FIELD_HEIGHT_MM, numberValue)));
};

export const DEFAULT_FIELD_ATTRS: PrintoutFieldAttrs = {
    align: "left",
    fillColor: null,
    heightMm: DEFAULT_FIELD_HEIGHT_MM,
    label: DEFAULT_FIELD_LABEL,
    labelStyle: {bold: false, italic: false},
    lineColor: DEFAULT_FIELD_LINE_COLOR,
    lineStyle: "underline",
};

export const getFieldAttrs = (attrs?: Partial<PrintoutFieldAttrs> | null): PrintoutFieldAttrs => ({
    align: parseEnumAttribute(attrs?.align, FIELD_ALIGNS, "left"),
    fillColor: parseColor(attrs?.fillColor, null),
    heightMm: clampFieldHeightMm(attrs?.heightMm),
    label: typeof attrs?.label === "string" ? attrs.label : DEFAULT_FIELD_LABEL,
    labelStyle: parseTextStyleAttribute(attrs?.labelStyle),
    lineColor: parseColor(attrs?.lineColor, DEFAULT_FIELD_LINE_COLOR) ?? DEFAULT_FIELD_LINE_COLOR,
    lineStyle: parseEnumAttribute(attrs?.lineStyle, FIELD_LINE_STYLES, "underline"),
});
